import React, { useState, useEffect, ReactNode } from "react";
import apiClient from "@shared/services/apiClient";
import { resolveOrgForLogin } from "@shared/services/resolveOrgClient";
import { useAuth } from "@shared/context/AuthContext";
import { OrganizationContext } from "./OrganizationContextStore";
import logger from "@shared/utils/logger";
import { getOrganizationLookupInputFromHostname } from "@shared/utils/orgLoginRouting";

// Organization interface matching backend model
interface Organization {
  organization_id: string;
  slug: string;
  name: string;
  domain: string | null;
  is_active: boolean;
  max_users: number | null;
  max_storage_gb: number | null;
  org_plan_tier?: string | null;
  orgPlanTier?: string | null;
  subscription_expires_at: string | null;
  billing_email: string | null;
  support_email: string | null;
  phone_number: string | null;
  address: string | null;
  created_at: string;
  updated_at: string;
}

interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  loading: boolean;
  error: string | null;
  fetchOrganizations: () => Promise<void>;
  fetchOrganizationBySlug: (slug: string) => Promise<Organization | null>;
  createOrganization: (data: Partial<Organization>) => Promise<Organization>;
  updateOrganization: (
    id: string,
    data: Partial<Organization>,
  ) => Promise<Organization>;
  deleteOrganization: (id: string) => Promise<void>;
  getOrganizationAddons: (id: string) => Promise<{
    telehealth: { enabled: boolean };
  }>;
  updateOrganizationAddons: (
    id: string,
    addons: { telehealth?: { enabled: boolean } },
  ) => Promise<{
    telehealth: { enabled: boolean };
  }>;
  setCurrentOrganization: (org: Organization | null) => void;
}

function normalizeOrganization(
  raw: Partial<Organization> | null | undefined,
): Organization | null {
  if (!raw?.organization_id || !raw.slug || !raw.name) {
    return null;
  }

  return {
    ...raw,
    organization_id: raw.organization_id,
    slug: raw.slug,
    name: raw.name,
    domain: raw.domain ?? null,
    is_active: Boolean(raw.is_active),
    max_users: raw.max_users ?? null,
    max_storage_gb: raw.max_storage_gb ?? null,
    org_plan_tier: raw.org_plan_tier ?? raw.orgPlanTier ?? null,
    orgPlanTier: raw.orgPlanTier ?? raw.org_plan_tier ?? null,
    subscription_expires_at: raw.subscription_expires_at ?? null,
    billing_email: raw.billing_email ?? null,
    support_email: raw.support_email ?? null,
    phone_number: raw.phone_number ?? null,
    address: raw.address ?? null,
    created_at: raw.created_at || "",
    updated_at: raw.updated_at || "",
  };
}

function buildOrganizationFromAuthenticatedUser(
  user:
    | {
        organization_id?: string;
        organization?: {
          organization_id: string;
          slug: string;
          name: string;
          domain?: string | null;
          is_active?: boolean;
          support_email?: string | null;
        } | null;
      }
    | null
    | undefined,
): Organization | null {
  const organization = user?.organization;
  if (
    !organization?.organization_id ||
    !organization.slug ||
    !organization.name
  ) {
    return null;
  }

  return normalizeOrganization({
    organization_id: organization.organization_id,
    slug: organization.slug,
    name: organization.name,
    domain: organization.domain ?? null,
    is_active: organization.is_active ?? true,
    support_email: organization.support_email ?? null,
  });
}

export const OrganizationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [currentOrganization, setCurrentOrganization] =
    useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // Fetch all organizations (SUPER_ADMIN only)
  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get("/organizations");
      const items = (response.data.data || response.data || [])
        .map((org: Organization) => normalizeOrganization(org))
        .filter(Boolean) as Organization[];
      setOrganizations(items);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to fetch organizations");
      logger.error("Failed to fetch organizations", err as Error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch organization by slug
  const fetchOrganizationBySlug = async (
    slug: string,
  ): Promise<Organization | null> => {
    try {
      const response = await apiClient.get(`/organizations/slug/${slug}`);
      const org = normalizeOrganization(response.data.data || response.data);
      return org;
    } catch (err: any) {
      logger.error(`Failed to fetch organization ${slug}`, err as Error);
      return null;
    }
  };

  // Create new organization (SUPER_ADMIN only)
  const createOrganization = async (
    data: Partial<Organization>,
  ): Promise<Organization> => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.post("/organizations", data);
      const newOrg = normalizeOrganization(response.data.data || response.data);
      if (!newOrg) {
        throw new Error("Failed to normalize organization response");
      }
      setOrganizations((prev) => [...prev, newOrg]);
      return newOrg;
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.error || "Failed to create organization";
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Update organization (SUPER_ADMIN only)
  const updateOrganization = async (
    id: string,
    data: Partial<Organization>,
  ): Promise<Organization> => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.patch(`/organizations/${id}`, data);
      const updatedOrg = normalizeOrganization(
        response.data.data || response.data,
      );
      if (!updatedOrg) {
        throw new Error("Failed to normalize organization response");
      }
      setOrganizations((prev) =>
        prev.map((org) => (org.organization_id === id ? updatedOrg : org)),
      );
      if (currentOrganization?.organization_id === id) {
        setCurrentOrganization(updatedOrg);
      }
      return updatedOrg;
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.error || "Failed to update organization";
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getOrganizationAddons = async (
    id: string,
  ): Promise<{ telehealth: { enabled: boolean } }> => {
    const response = await apiClient.get(`/organizations/${id}/addons`);
    return response.data?.data?.addons || { telehealth: { enabled: false } };
  };

  const updateOrganizationAddons = async (
    id: string,
    addons: { telehealth?: { enabled: boolean } },
  ): Promise<{ telehealth: { enabled: boolean } }> => {
    const response = await apiClient.put(`/organizations/${id}/addons`, addons);
    const updated = response.data?.data?.addons || {
      telehealth: { enabled: false },
    };

    return updated;
  };

  // Delete organization (SUPER_ADMIN only)
  const deleteOrganization = async (id: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await apiClient.delete(`/organizations/${id}`);
      setOrganizations((prev) =>
        prev.filter((org) => org.organization_id !== id),
      );
      if (currentOrganization?.organization_id === id) {
        setCurrentOrganization(null);
      }
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.error || "Failed to delete organization";
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Load current organization from the active hostname. Shared platform
  // subdomains resolve by slug; branded customer domains resolve by exact host.
  useEffect(() => {
    const lookupInput = getOrganizationLookupInputFromHostname(
      window.location.hostname,
    );

    if (!lookupInput) {
      logger.debug(
        "[OrganizationContext] Development mode - tenant hydration deferred to auth context",
      );
      return;
    }

    const loadCurrentOrganization = () => {
      resolveOrgForLogin(lookupInput)
        .then((data) => {
          if (data?.resolved && data.organization) {
            const resolved = data.organization;
            setCurrentOrganization({
              organization_id: resolved.organization_id ?? "",
              slug: resolved.slug ?? "",
              name: resolved.name ?? "",
              domain: resolved.domain ?? null,
              is_active: true,
              max_users: null,
              max_storage_gb: null,
              subscription_expires_at: null,
              billing_email: null,
              support_email: null,
              phone_number: null,
              address: null,
              created_at: "",
              updated_at: "",
            });
          }
        })
        .catch(() => {
          // Pre-auth resolve failures are non-fatal
        });
    };

    loadCurrentOrganization();

    const intervalId = window.setInterval(loadCurrentOrganization, 5 * 60 * 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
    if (!isLocalhost) {
      return;
    }

    const hydratedOrganization = buildOrganizationFromAuthenticatedUser(user);
    if (hydratedOrganization) {
      setCurrentOrganization((previous) => {
        if (
          previous?.organization_id === hydratedOrganization.organization_id &&
          previous?.name === hydratedOrganization.name &&
          previous?.slug === hydratedOrganization.slug &&
          previous?.support_email === hydratedOrganization.support_email
        ) {
          return previous;
        }
        return hydratedOrganization;
      });
      return;
    }

    if (!user) {
      setCurrentOrganization(null);
    }
  }, [user]);

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganization,
        organizations,
        loading,
        error,
        fetchOrganizations,
        fetchOrganizationBySlug,
        createOrganization,
        updateOrganization,
        deleteOrganization,
        getOrganizationAddons,
        updateOrganizationAddons,
        setCurrentOrganization,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export type { Organization, OrganizationContextType };
